function parseAbsoluteHttpUrl(value: string): URL | undefined {
  try {
    const parsed = new URL(value);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return undefined;
    }
    if (parsed.username || parsed.password) {
      return undefined;
    }
    return parsed;
  } catch {
    return undefined;
  }
}

function parseBrowserOrigin(value: string): URL | undefined {
  const origin = parseAbsoluteHttpUrl(value);
  if (
    !origin ||
    origin.pathname !== '/' ||
    origin.search !== '' ||
    origin.hash !== ''
  ) {
    return undefined;
  }
  return origin;
}

function parseHttpUrl(value: string, browserOrigin?: URL): URL | undefined {
  const absolute = parseAbsoluteHttpUrl(value);
  if (absolute) {
    return absolute;
  }
  if (!browserOrigin || !value.startsWith('/') || value.startsWith('//')) {
    return undefined;
  }
  try {
    return new URL(value, browserOrigin);
  } catch {
    return undefined;
  }
}

function effectivePort(url: URL): string {
  if (url.port) {
    return url.port;
  }
  return url.protocol === 'https:' ? '443' : '80';
}

function apiBasePath(pathname: string): string {
  const withoutTrailingSlashes = pathname.replace(/\/+$/, '');
  return withoutTrailingSlashes || '/';
}

/**
 * Decides whether a browser request may receive the Keycloak bearer token.
 *
 * Normal application profiles use an absolute HTTP(S) endpoint on the
 * dedicated API origin. Root-relative endpoints remain supported for the
 * isolated synthetic application profile and are resolved only against the
 * supplied exact browser origin. User information, queries, fragments,
 * deceptive origins, and path-boundary escapes fail closed. URL parsing
 * normalizes dot segments before comparison.
 */
export function shouldAttachApiToken(
  requestUrl: string,
  configuredApiEndpoint: string,
  browserOrigin?: string
): boolean {
  const parsedBrowserOrigin =
    browserOrigin === undefined ? undefined : parseBrowserOrigin(browserOrigin);
  if (browserOrigin !== undefined && !parsedBrowserOrigin) {
    return false;
  }

  const request = parseHttpUrl(requestUrl, parsedBrowserOrigin);
  const endpoint = parseHttpUrl(configuredApiEndpoint, parsedBrowserOrigin);

  if (!request || !endpoint || endpoint.search || endpoint.hash) {
    return false;
  }

  if (
    request.protocol !== endpoint.protocol ||
    request.hostname !== endpoint.hostname ||
    effectivePort(request) !== effectivePort(endpoint)
  ) {
    return false;
  }

  const basePath = apiBasePath(endpoint.pathname);
  if (basePath === '/') {
    return true;
  }

  return (
    request.pathname === basePath ||
    request.pathname.startsWith(`${basePath}/`)
  );
}
